'use client'

import Image from 'next/image'
import { Play } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/styles/cn'
import { useLandingAuthContext } from '@/features/landing-page/landing-auth-context'
import { useLandingMediaOverlay } from '@/features/landing-page/landing-media-overlay-context'
import { LAUNCH_VIDEO_SRC } from '@/features/landing-page/v3/launch-video'
import { LP_GRID } from '@/features/landing-page/v3/lp-grid'

/**
 * lp-v3 hero — first section of the redesigned logged-out landing. Two columns on desktop (copy left,
 * floating product-card collage right) that stack on mobile.
 *
 * Copy is per-page: `title` (a `\n`-separated string; the LAST line renders in the orange pixel face)
 * and `subtitle` come from the content system (getLandingRouteContentV3). The right-side collage is
 * constant across pages. When the copy changes (client-side nav) it cross-fades — gated so SSR/first
 * render is always opacity:1 (never bakes opacity:0 into the HTML).
 *
 * The collage is composed from seven individual card PNGs (was a single flat `hero-collage.png`).
 * The "Introducing superscale agents" card is a real button that opens the launch film in the shared
 * media overlay — same YouTube video the vision section plays via VisionFilmButton.
 *
 * `!` (important) overrides the `.ds2` element typography that still wraps the shell.
 */

interface ILpHeroSectionProps {
    title: string
    subtitle: string
    ctaLabel?: string
    /** Optional node rendered under the CTA pair (e.g. the home page's agency routing note). */
    belowCta?: React.ReactNode
}

const FADE_MS = 200
const CARDS = '/assets/landing-v3/hero-cards'

/** One floating card in the hero collage. Positions are % of the aspect-square container. */
type HeroCard = {
    key: string
    src: string
    alt: string
    /** native w/h — used to preserve aspect ratio when we set `width`. */
    width: number
    height: number
    /** percentage placement inside the square container. */
    left: string
    top: string
    widthPct: string
    /** z-order — later renders on top. */
    z: number
}

// Positions come STRAIGHT from Figma dev mode (frame ≈ 759 × 769, matches aspect-square).
// Each card's Left/Top/Width is expressed as a % of that frame so it scales fluidly. Native
// image aspects match the Figma widths/heights exactly, so we don't need to set height — it's
// derived by `h-auto` from `widthPct`.
// z-order (back → front): people → prompt → competitor → furniture → campaign-video →
// website-research → launch-video (front-most, interactive).
const HERO_CARDS: HeroCard[] = [
    { key: 'people-campaign',     src: `${CARDS}/people-campaign.png`,          alt: 'People-campaign creative shoot',           width: 756,  height: 828,  left: '36.9%', top: '0%',    widthPct: '33.2%', z: 1 },
    { key: 'prompt',              src: `${CARDS}/prompt-card.png`,              alt: 'Prompt: build buzz around a retro sneaker drop', width: 639,  height: 621,  left: '15.0%', top: '11.9%', widthPct: '28.1%', z: 2 },
    { key: 'competitor-analysis', src: `${CARDS}/competitor-analysis-card.png`, alt: 'Competitor analysis — Competitive Social Landscape', width: 738, height: 645, left: '67.6%', top: '21.1%', widthPct: '32.4%', z: 3 },
    { key: 'furniture',           src: `${CARDS}/furniture.png`,                alt: 'Furniture product shot — caned lounge chair', width: 594, height: 1128, left: '60.3%', top: '42.8%', widthPct: '26.1%', z: 4 },
    { key: 'campaign-video',      src: `${CARDS}/campaign-video-poster.png`,    alt: 'Campaign video poster — two people dancing', width: 855,  height: 1141, left: '27.5%', top: '50.6%', widthPct: '37.5%', z: 5 },
    { key: 'website-research',    src: `${CARDS}/website-research-card.png`,    alt: 'Website research in progress — drinkag1.com',  width: 675,  height: 615,  left: '45.3%', top: '62.1%', widthPct: '29.6%', z: 6 },
]

/**
 * The "Introducing superscale agents" card is INTERACTIVE — it opens the launch film overlay.
 * Kept at the highest z-index so its click target is never obscured by another card.
 *
 * The thumbnail is the first frame of hero-loop-v3.mp4 (16:9). The "Introducing superscale agents"
 * title bar and "Watch launch video" chip are rendered in HTML (they used to be baked into the
 * old launch-video-poster.png).
 */
const LAUNCH_VIDEO_CARD = {
    src: `${CARDS}/launch-video-first-frame.png`,
    alt: 'Introducing Superscale agents — click to watch the launch film',
    /** intrinsic dimensions of the extracted first frame. */
    width: 1920,
    height: 1080,
    left: '0%',
    top: '33.0%',
    widthPct: '31.4%',
    z: 10,
}

export function LpHeroSection({ title, subtitle, ctaLabel = 'Get started', belowCta }: ILpHeroSectionProps) {
    const { handleCtaClick } = useLandingAuthContext()

    // Crossfade the copy on change: fade out → swap displayed copy → fade in. Image stays mounted.
    // belowCta joins the swap by PRESENCE (nodes are recreated every render, so identity can't gate
    // the fade) — the agency note leaves/arrives with the copy instead of popping mid-fade.
    const [displayed, setDisplayed] = useState({ title, subtitle, ctaLabel, belowCta })
    const [visible, setVisible] = useState(true)

    useEffect(() => {
        if (
            title === displayed.title &&
            subtitle === displayed.subtitle &&
            ctaLabel === displayed.ctaLabel &&
            !!belowCta === !!displayed.belowCta
        ) {
            return
        }
        setVisible(false)
        const timeout = setTimeout(() => {
            setDisplayed({ title, subtitle, ctaLabel, belowCta })
            setVisible(true)
        }, FADE_MS)
        return () => clearTimeout(timeout)
    }, [title, subtitle, ctaLabel, belowCta, displayed])

    const lines = displayed.title.split('\n')

    return (
        <section className={`${LP_GRID.contentSection} py-[48px] md:py-[40px]`}>
            <div className="mx-auto max-w-[1320px]">
                {/* Hero fills one viewport under the fixed nav: 100svh − 64px nav − 80px section pad.
                    Copy left · floating collage right. No chalk panel — the collage sits on cream. */}
                <div className="flex flex-col gap-10 md:grid md:min-h-[clamp(520px,calc(100svh_-_144px),1080px)] md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.3fr)] md:items-center md:gap-6 lg:gap-10">
                    {/* Copy — cross-fades on page change */}
                    <div className="flex items-center md:self-center">
                        <div
                            className={cn(
                                'max-w-[428px] transition-opacity duration-200 ease-out',
                                visible ? 'opacity-100' : 'opacity-0'
                            )}
                        >
                            <h1 className="text-[32px]! font-medium leading-[1.1]! tracking-[-0.02em]! lg:text-[40px]!">
                                {lines.map((line, index) => {
                                    const isLast = index === lines.length - 1
                                    return (
                                        <span
                                            key={index}
                                            className={cn(
                                                'block',
                                                isLast
                                                    ? 'font-[family-name:var(--font-landing-pixel)]! font-bold tracking-normal! text-[#FF5500]!'
                                                    : 'text-foreground'
                                            )}
                                        >
                                            {line}
                                        </span>
                                    )
                                })}
                            </h1>
                            <p className="mt-5 max-w-[400px] text-[16px]! leading-[1.3]! tracking-[-0.02em]! text-muted-foreground md:text-[18px]! md:leading-[1.25]!">
                                {displayed.subtitle}
                            </p>
                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <Button
                                    variant="lp-primary"
                                    size="lp-lg"
                                    onClick={handleCtaClick}
                                    data-cta="get_started"
                                    data-section="hero"
                                >
                                    {displayed.ctaLabel}
                                </Button>
                            </div>
                            {displayed.belowCta}
                        </div>
                    </div>

                    {/* Floating collage — full-bleed in its column, no panel chrome. */}
                    <div className="relative flex w-full items-center justify-center md:justify-end">
                        <HeroCollage />
                    </div>
                </div>
            </div>
        </section>
    )
}

/**
 * The right-column card stack. Container is `aspect-square` so absolute-positioned children (by %)
 * scale linearly with viewport width. Each card preserves its native aspect ratio via width/height
 * on next/image with `w-full h-auto`. The launch-video card is a real button that opens the shared
 * media overlay with the same YouTube video the vision-section film widget uses.
 */
function HeroCollage() {
    const overlay = useLandingMediaOverlay()
    const openLaunchFilm = () =>
        overlay.open({
            items: [{ type: 'video', src: LAUNCH_VIDEO_SRC, aspect: 'landscape', alt: 'Superscale launch film' }],
        })

    return (
        // aspect-square so the % positions inside scale as one unit. Capped on desktop so the
        // collage doesn't dominate on very wide screens; on mobile the cards already respect
        // the section's px-4 gutter via `max-w-[560px]` + centered layout.
        <div className="relative aspect-square w-full max-w-[560px] select-none md:max-w-[620px]">
            {HERO_CARDS.map((card) => (
                <div
                    key={card.key}
                    className="absolute"
                    style={{ left: card.left, top: card.top, width: card.widthPct, zIndex: card.z }}
                >
                    <Image
                        src={card.src}
                        alt={card.alt}
                        width={card.width}
                        height={card.height}
                        sizes="(max-width: 768px) 45vw, 260px"
                        // Only the largest / centre-most cards preempt the fetch queue as LCP
                        // candidates. Marking all 7 `priority` triggers competing preloads and
                        // regresses LCP on slow networks.
                        priority={card.key === 'campaign-video' || card.key === 'people-campaign'}
                        className="h-auto w-full"
                    />
                </div>
            ))}
            {/* Launch-video card — real button, opens the launch film in the shared overlay.
                Chrome (title bar + "Watch launch video" chip) is HTML now that the thumbnail is the
                raw first frame of the video (was previously baked into launch-video-poster.png). */}
            <button
                type="button"
                onClick={openLaunchFilm}
                aria-label="Watch the Superscale launch film"
                className="group absolute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500] focus-visible:ring-offset-2"
                style={{
                    left: LAUNCH_VIDEO_CARD.left,
                    top: LAUNCH_VIDEO_CARD.top,
                    width: LAUNCH_VIDEO_CARD.widthPct,
                    zIndex: LAUNCH_VIDEO_CARD.z,
                }}
            >
                <div className="overflow-hidden rounded-[10px] bg-white shadow-[0_10px_30px_rgba(0,0,0,0.10)] transition-transform duration-200 group-hover:scale-[1.02]">
                    <div className="flex items-center bg-[#F0EEEA] px-2 py-1.5 text-[9px] font-medium tracking-[-0.01em] text-[#746C64] md:text-[10px]">
                        Introducing superscale agents
                    </div>
                    <div className="relative">
                        <Image
                            src={LAUNCH_VIDEO_CARD.src}
                            alt={LAUNCH_VIDEO_CARD.alt}
                            width={LAUNCH_VIDEO_CARD.width}
                            height={LAUNCH_VIDEO_CARD.height}
                            sizes="(max-width: 768px) 40vw, 260px"
                            priority
                            className="block h-auto w-full"
                        />
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-medium leading-none text-white shadow-lg md:gap-2 md:px-3 md:py-1.5 md:text-[11px]">
                                <Play className="size-2.5 translate-x-[0.5px] fill-white md:size-3" />
                                Watch video
                            </span>
                        </span>
                    </div>
                </div>
            </button>
        </div>
    )
}
